import React, { useEffect, useState } from 'react';
import { dateFormater } from '@app/utils/functions';
import { 
    Button, CalendarMonth, Dropdown, DropdownItem, DropdownList, 
    InputGroup, InputGroupItem, MenuToggle, MenuToggleElement, 
    Popover, TextInput 
} from '@patternfly/react-core';
import { OutlinedCalendarAltIcon, OutlinedClockIcon } from '@patternfly/react-icons';

interface DateTimePickerProps extends Omit<React.HTMLProps<HTMLInputElement>, 'onChange' | 'value' | 'disabled' | 'type'> {
    value?: Date | string | null;
    onChange: (event: any, newValue: Date) => void;
}

// `label` is pulled out of props and dropped on purpose: it comes from React.HTMLProps and
// would otherwise land on the input element via {...props}.
const DateTimePicker: React.FC<DateTimePickerProps> = ({ label: _label, value, onChange, id, ...props }) => {
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [isTimeOpen, setIsTimeOpen] = useState(false);
    
    // Default placeholders
    const [valueDate, setValueDate] = useState('dd/mm/yyyy');
    const [valueTime, setValueTime] = useState('HH:MM');
    const defaultTime = '00:00';

    // FIXED 1: Synchronize incoming `value` prop with local state
    useEffect(() => {
        if (value) {
            const dateObj = typeof value === 'string' ? new Date(value) : value;
            if (!isNaN(dateObj.getTime())) {
                setValueDate(dateFormater(dateObj)); // Assuming this returns formatted string
                
                // Format time to HH:mm with padding (e.g., 08:00)
                const hh = String(dateObj.getHours()).padStart(2, '0');
                const mm = String(dateObj.getMinutes()).padStart(2, '0');
                setValueTime(`${hh}:${mm}`);
            }
        } else {
            setValueDate('dd/mm/yyyy');
            setValueTime('HH:MM');
        }
    }, [value]);

    const onToggleCalendar = () => {
        setIsCalendarOpen(!isCalendarOpen);
        setIsTimeOpen(false); // Close time if calendar opens
    };

    const onToggleTime = () => {
        setIsTimeOpen(!isTimeOpen);
        setIsCalendarOpen(false); // Close calendar if time opens
    };

    /**
     * The chosen day at the chosen time.
     *
     * Built from the Date the calendar handed us rather than by formatting it and parsing it back.
     * That round trip is what used to break this: dateFormater writes dd/mm/yyyy with slashes,
     * dateTimeParser splits the date on hyphens, so it never recognised its own input and returned
     * `new Date()` — every selection, on every screen using this, silently became "now".
     */
    const withTime = (date: Date, time: string): Date => {
        const [hours, minutes] = time.split(':').map(Number);
        const combined = new Date(date);
        combined.setHours(hours ?? 0, minutes ?? 0, 0, 0);
        return combined;
    };

    const onSelectCalendar = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, newValueDate: Date) => {
        setValueDate(dateFormater(newValueDate));
        setIsCalendarOpen(false);

        const currentTime = valueTime === 'HH:MM' ? defaultTime : valueTime;
        setValueTime(currentTime);

        onChange(event, withTime(newValueDate, currentTime));
    };

    // FIXED 2: Actually trigger onChange when the time is selected
    const onSelectTime = (event: React.MouseEvent<Element, MouseEvent> | undefined, selectedTime: string | number | undefined) => {
        const newTime = String(selectedTime);
        setValueTime(newTime);
        setIsTimeOpen(false);

        // The day already chosen, or today if they picked a time first. Taken from `value` because
        // that is the Date this component was last given - valueDate is only its display text.
        const baseDate = value ? new Date(value) : new Date();
        if (valueDate === 'dd/mm/yyyy') {
            setValueDate(dateFormater(baseDate));
        }

        onChange(event, withTime(baseDate, newTime));
    };

    // FIXED 4: Pad single-digit hours with zero for standard parser compatibility
    const times = Array.from(new Array(10), (_, i) => i + 8); // 8 to 17
    const timeOptions = times.map((time) => {
        const formattedTime = `${String(time).padStart(2, '0')}:00`;
        return (
            <DropdownItem key={formattedTime} value={formattedTime}>
                {formattedTime}
            </DropdownItem>
        );
    });

    const calendar = <CalendarMonth date={value ? new Date(value) : new Date()} onChange={onSelectCalendar} />;

    const time = (
        <Dropdown
            onSelect={onSelectTime}
            isOpen={isTimeOpen}
            onOpenChange={(isOpen: boolean) => setIsTimeOpen(isOpen)}
            toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
                <MenuToggle
                    ref={toggleRef}
                    onClick={onToggleTime}
                    isExpanded={isTimeOpen}
                    aria-label="Time picker"
                    icon={<OutlinedClockIcon aria-hidden="true" />}
                />
            )}
        >
            <DropdownList>{timeOptions}</DropdownList>
        </Dropdown>
    );

    const calendarButton = (
        <Button
            variant="control"
            aria-label="Toggle the calendar"
            onClick={onToggleCalendar}
            icon={<OutlinedCalendarAltIcon />}
        />
    );

    // Dynamic display text based on whether data actually exists
    const displayString = valueDate !== 'dd/mm/yyyy' ? `${valueDate} ${valueTime}` : '';

    return (
        <div style={{ width: '300px' }}>
            <Popover
                position="bottom"
                bodyContent={calendar}
                showClose={false}
                isVisible={isCalendarOpen}
                hasNoPadding
                hasAutoWidth
            >
                <InputGroup>
                    <InputGroupItem isFill>
                        <TextInput
                            type="text"
                            id={id || "date-time"}
                            aria-label="date and time picker input"
                            value={displayString}
                            placeholder="dd/mm/yyyy HH:MM"
                            readOnlyVariant="default"
                            {...props}
                        />
                    </InputGroupItem>
                    <InputGroupItem>{calendarButton}</InputGroupItem>
                    <InputGroupItem>{time}</InputGroupItem>
                </InputGroup>
            </Popover>
        </div>
    );
};

export default DateTimePicker;